import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Edit3,
  Eye,
  EyeOff,
  Plus,
  RefreshCw,
  Smartphone,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';

import PageHeader from '@/components/PageHeader';
import Modal from '@/components/Modal';
import ImageUploadField from '@/components/ImageUploadField';
import {
  createAppBanner,
  deleteAppBanner,
  listAppBanners,
  listTenants,
  mediaUrl,
  updateAppBanner,
  type AppBanner,
  type AppBannerInput,
  type BannerAction,
  type TenantListItem,
} from '@/lib/operatorApi';
import { formatDateTime } from '@/lib/format';

// Баннеры карусели на главной гостевого приложения (People).
// Порядок — sortOrder (меньше — левее); стрелками меняем местами соседей.
// Показ ограничивается окном startsAt/endsAt и флагом isActive — телефон
// получает только «живые» баннеры через /api/public/app/home.

type EditMode = { kind: 'create' } | { kind: 'edit'; banner: AppBanner } | null;

const ACTION_LABEL: Record<BannerAction, string> = {
  NONE: 'Ничего',
  URL: 'Открыть ссылку',
  ESTABLISHMENT: 'Открыть заведение',
};

function isLive(b: AppBanner, now: number): boolean {
  if (!b.isActive) return false;
  if (b.startsAt && new Date(b.startsAt).getTime() > now) return false;
  if (b.endsAt && new Date(b.endsAt).getTime() < now) return false;
  return true;
}

function scheduleLabel(b: AppBanner, now: number): string | null {
  if (b.startsAt && new Date(b.startsAt).getTime() > now) return `с ${formatDateTime(b.startsAt)}`;
  if (b.endsAt && new Date(b.endsAt).getTime() < now) return `истёк ${formatDateTime(b.endsAt)}`;
  if (b.endsAt) return `до ${formatDateTime(b.endsAt)}`;
  return null;
}

export default function AppBanners() {
  const [banners, setBanners] = useState<AppBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditMode>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listAppBanners()
      .then((r) => setBanners(r.banners))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const now = Date.now();
  const liveCount = useMemo(() => banners.filter((b) => isLive(b, now)).length, [banners, now]);

  const run = async (id: string, fn: () => Promise<unknown>) => {
    try {
      setBusyId(id);
      await fn();
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = (b: AppBanner) =>
    run(b.id, () => updateAppBanner(b.id, { isActive: !b.isActive }));

  const remove = (b: AppBanner) => {
    if (!window.confirm(`Удалить баннер «${b.title ?? 'без названия'}»?`)) return;
    void run(b.id, () => deleteAppBanner(b.id));
  };

  // Меняем местами sortOrder соседей. Если значения совпадают (оба 0) —
  // раздаём последовательные номера всему списку, чтобы порядок стал явным.
  const move = async (index: number, dir: -1 | 1) => {
    const other = index + dir;
    if (other < 0 || other >= banners.length) return;
    const a = banners[index];
    const b = banners[other];
    setBusyId(a.id);
    try {
      if (a.sortOrder === b.sortOrder) {
        const reordered = [...banners];
        [reordered[index], reordered[other]] = [reordered[other], reordered[index]];
        await Promise.all(reordered.map((x, i) => updateAppBanner(x.id, { sortOrder: i })));
      } else {
        await Promise.all([
          updateAppBanner(a.id, { sortOrder: b.sortOrder }),
          updateAppBanner(b.id, { sortOrder: a.sortOrder }),
        ]);
      }
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Гостевое приложение"
        title="Баннеры на главной"
        description="Карусель в шапке приложения People. Порядок — сверху вниз, показ можно ограничить датами. Телефоны подхватывают изменения в течение минуты."
        actions={
          <div className="flex items-center gap-2">
            <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
              <RefreshCw size={13} strokeWidth={1.75} />
              Обновить
            </button>
            <button type="button" className="btn-primary" onClick={() => setEdit({ kind: 'create' })}>
              <Plus size={13} strokeWidth={1.75} />
              Новый баннер
            </button>
          </div>
        }
      />

      <div className="page-pad">
        {error && (
          <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-2 text-[13px] text-red-700">
            {error}
          </div>
        )}

        <div className="mb-4 flex items-center gap-3 text-[13px] text-ink/60">
          <Smartphone size={14} strokeWidth={1.75} />
          Сейчас в приложении: <span className="font-medium text-ink">{liveCount}</span> из {banners.length}
        </div>

        {loading && banners.length === 0 ? (
          <div className="text-ink/50">Загрузка…</div>
        ) : banners.length === 0 ? (
          <div className="card p-10 text-center text-[14px] text-ink/50">
            Баннеров пока нет. Добавьте первый — он сразу появится на главной приложения.
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {banners.map((b, i) => {
              const live = isLive(b, now);
              const schedule = scheduleLabel(b, now);
              const busy = busyId === b.id;
              return (
                <li key={b.id} className={clsx('card overflow-hidden', busy && 'opacity-60')}>
                  <div
                    className="relative aspect-[16/7] bg-paper"
                    style={b.bgColor ? { backgroundColor: b.bgColor } : undefined}
                  >
                    <img
                      src={mediaUrl(b.imageUrl)}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute left-3 top-3 flex gap-2">
                      <span className={live ? 'badge-green' : 'badge-ink'}>
                        {live ? 'В эфире' : b.isActive ? 'По расписанию' : 'Выключен'}
                      </span>
                      {b.action !== 'NONE' && <span className="badge-orange">{ACTION_LABEL[b.action]}</span>}
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-medium tracking-tight">
                        {b.title || <span className="text-ink/40">Без заголовка</span>}
                      </div>
                      {b.subtitle && <div className="mt-0.5 truncate text-[13px] text-ink/60">{b.subtitle}</div>}
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-ink/50">
                        <span>Порядок: {b.sortOrder}</span>
                        {schedule && <span>{schedule}</span>}
                        {b.action === 'URL' && b.actionValue && (
                          <a href={b.actionValue} target="_blank" rel="noreferrer" className="link truncate max-w-[220px]">
                            {b.actionValue}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        className="btn-ghost !p-2"
                        title="Выше"
                        onClick={() => void move(i, -1)}
                        disabled={busy || i === 0}
                      >
                        <ArrowUp size={14} strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        className="btn-ghost !p-2"
                        title="Ниже"
                        onClick={() => void move(i, 1)}
                        disabled={busy || i === banners.length - 1}
                      >
                        <ArrowDown size={14} strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        className="btn-ghost !p-2"
                        title={b.isActive ? 'Выключить' : 'Включить'}
                        onClick={() => void toggleActive(b)}
                        disabled={busy}
                      >
                        {b.isActive ? <Eye size={14} strokeWidth={1.75} /> : <EyeOff size={14} strokeWidth={1.75} />}
                      </button>
                      <button
                        type="button"
                        className="btn-ghost !p-2"
                        title="Редактировать"
                        onClick={() => setEdit({ kind: 'edit', banner: b })}
                        disabled={busy}
                      >
                        <Edit3 size={14} strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        className="btn-ghost !p-2 text-red-600"
                        title="Удалить"
                        onClick={() => remove(b)}
                        disabled={busy}
                      >
                        <Trash2 size={14} strokeWidth={1.75} />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {edit && (
        <BannerModal
          mode={edit}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ─── Форма ────────────────────────────────────────────────────────────

interface Draft {
  title: string;
  subtitle: string;
  imageUrl: string | null;
  action: BannerAction;
  actionUrl: string;
  actionEstablishmentId: string;
  bgColor: string;
  sortOrder: number;
  isActive: boolean;
  startsAt: string; // datetime-local
  endsAt: string;
}

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function toDraft(b: AppBanner | null): Draft {
  return {
    title: b?.title ?? '',
    subtitle: b?.subtitle ?? '',
    imageUrl: b?.imageUrl ?? null,
    action: b?.action ?? 'NONE',
    actionUrl: b?.action === 'URL' ? b.actionValue ?? '' : '',
    actionEstablishmentId: b?.action === 'ESTABLISHMENT' ? b.actionValue ?? '' : '',
    bgColor: b?.bgColor ?? '',
    sortOrder: b?.sortOrder ?? 0,
    isActive: b?.isActive ?? true,
    startsAt: toLocalInput(b?.startsAt ?? null),
    endsAt: toLocalInput(b?.endsAt ?? null),
  };
}

function BannerModal({
  mode,
  onClose,
  onSaved,
}: {
  mode: NonNullable<EditMode>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = mode.kind === 'edit' ? mode.banner : null;
  const [draft, setDraft] = useState<Draft>(() => toDraft(editing));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantListItem[]>([]);

  // Список заведений нужен только для действия «Открыть заведение».
  useEffect(() => {
    if (draft.action !== 'ESTABLISHMENT' || tenants.length > 0) return;
    listTenants({ limit: 200 })
      .then((r) => setTenants(r.results))
      .catch(() => setTenants([]));
  }, [draft.action, tenants.length]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!draft.imageUrl) {
      setError('Загрузите картинку баннера');
      return;
    }
    if (draft.action === 'URL' && !/^https?:\/\//i.test(draft.actionUrl.trim())) {
      setError('Ссылка должна начинаться с http:// или https://');
      return;
    }
    if (draft.action === 'ESTABLISHMENT' && !draft.actionEstablishmentId) {
      setError('Выберите заведение');
      return;
    }
    const startsAt = fromLocalInput(draft.startsAt);
    const endsAt = fromLocalInput(draft.endsAt);
    if (startsAt && endsAt && startsAt > endsAt) {
      setError('Окончание показа раньше начала');
      return;
    }

    const body: AppBannerInput = {
      title: draft.title.trim() || null,
      subtitle: draft.subtitle.trim() || null,
      imageUrl: draft.imageUrl,
      action: draft.action,
      actionValue:
        draft.action === 'URL'
          ? draft.actionUrl.trim()
          : draft.action === 'ESTABLISHMENT'
            ? draft.actionEstablishmentId
            : null,
      bgColor: draft.bgColor.trim() || null,
      sortOrder: draft.sortOrder,
      isActive: draft.isActive,
      startsAt,
      endsAt,
    };

    setSaving(true);
    try {
      if (editing) await updateAppBanner(editing.id, body);
      else await createAppBanner(body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editing ? 'Баннер' : 'Новый баннер'} onClose={onClose} wide>
      <form onSubmit={submit} className="grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <div>
            <label className="eyebrow mb-2 block">Картинка</label>
            <ImageUploadField
              kind="banner"
              value={draft.imageUrl}
              onChange={(url) => set('imageUrl', url)}
              aspect="wide"
              hint="16:9, до 15 МБ — ужмём до 1600px"
              disabled={saving}
            />
          </div>

          <div>
            <label className="eyebrow mb-1 block">Цвет подложки</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={/^#[0-9a-f]{6}$/i.test(draft.bgColor) ? draft.bgColor : '#f2f1ee'}
                onChange={(e) => set('bgColor', e.target.value)}
                className="h-9 w-12 cursor-pointer border border-line bg-white p-1"
                disabled={saving}
              />
              <input
                className="input font-mono"
                placeholder="#RRGGBB"
                value={draft.bgColor}
                onChange={(e) => set('bgColor', e.target.value)}
                maxLength={7}
                disabled={saving}
              />
              {draft.bgColor && (
                <button type="button" className="btn-ghost !px-2 text-[12px]" onClick={() => set('bgColor', '')}>
                  Сброс
                </button>
              )}
            </div>
            <p className="mt-1 text-[12px] text-ink/50">Показывается пока картинка грузится.</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="eyebrow mb-1 block">Заголовок</label>
            <input
              className="input"
              value={draft.title}
              onChange={(e) => set('title', e.target.value)}
              maxLength={80}
              placeholder="Необязательно"
              disabled={saving}
            />
          </div>
          <div>
            <label className="eyebrow mb-1 block">Подзаголовок</label>
            <input
              className="input"
              value={draft.subtitle}
              onChange={(e) => set('subtitle', e.target.value)}
              maxLength={160}
              placeholder="Необязательно"
              disabled={saving}
            />
          </div>

          <div>
            <label className="eyebrow mb-1 block">По тапу</label>
            <select
              className="input"
              value={draft.action}
              onChange={(e) => set('action', e.target.value as BannerAction)}
              disabled={saving}
            >
              {(Object.keys(ACTION_LABEL) as BannerAction[]).map((a) => (
                <option key={a} value={a}>
                  {ACTION_LABEL[a]}
                </option>
              ))}
            </select>
            {draft.action === 'URL' && (
              <input
                className="input mt-2"
                type="url"
                placeholder="https://…"
                value={draft.actionUrl}
                onChange={(e) => set('actionUrl', e.target.value)}
                disabled={saving}
              />
            )}
            {draft.action === 'ESTABLISHMENT' && (
              <select
                className="input mt-2"
                value={draft.actionEstablishmentId}
                onChange={(e) => set('actionEstablishmentId', e.target.value)}
                disabled={saving}
              >
                <option value="">— выберите заведение —</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.city ? ` · ${t.city}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow mb-1 block">Показывать с</label>
              <input
                className="input"
                type="datetime-local"
                value={draft.startsAt}
                onChange={(e) => set('startsAt', e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <label className="eyebrow mb-1 block">до</label>
              <input
                className="input"
                type="datetime-local"
                value={draft.endsAt}
                onChange={(e) => set('endsAt', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow mb-1 block">Порядок</label>
              <input
                className="input"
                type="number"
                value={draft.sortOrder}
                onChange={(e) => set('sortOrder', Number(e.target.value) || 0)}
                disabled={saving}
              />
            </div>
            <label className="flex items-end gap-2 pb-2 text-[13px]">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
                disabled={saving}
                className="h-4 w-4 accent-orange"
              />
              Включён
            </label>
          </div>
        </div>

        {error && (
          <p className="md:col-span-2 text-[13px] text-red-600">{error}</p>
        )}

        <div className="flex justify-end gap-2 md:col-span-2">
          <button type="button" className="btn-outline" onClick={onClose} disabled={saving}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Сохраняем…' : editing ? 'Сохранить' : 'Создать'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
